/**
 * Academy V2 - Professional & User-Friendly Learning Platform
 *
 * Features:
 * - Learning paths with progress tracking
 * - Achievement system and badges
 * - Personalized recommendations
 * - Stats dashboard
 * - Improved search and filters
 * - Certificate generation
 */

import { useState, useMemo, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { useToast } from '../components/Toast';
import { HeroBanner } from '../components/academy/HeroBanner';
import { CourseRow } from '../components/academy/CourseRow';
import { VideoPlayerModal } from '../components/academy/VideoPlayer';
import {
  MOCK_COURSES,
  getFeaturedCourses,
  getTrendingCourses,
  getCoursesByLevel,
  getCoursesByCategory
} from '../data/courses';
import type { Course, UserProgress } from '../types/academy';

// LocalStorage keys
const STORAGE_KEYS = {
  PROGRESS: 'edouard_ai_academy_progress',
  MY_LIST: 'edouard_ai_academy_mylist',
  ACHIEVEMENTS: 'edouard_ai_academy_achievements',
  PREFERENCES: 'edouard_ai_academy_preferences'
};

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: Date;
}

interface LearningPath {
  id: string;
  title: string;
  description: string;
  courses: string[]; // Course IDs
  icon: string;
  color: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

const LEARNING_PATHS: LearningPath[] = [
  {
    id: 'crypto-basics',
    title: '🎓 Crypto Basics',
    description: 'Start your crypto journey from zero',
    courses: ['intro-crypto', 'blockchain-basics', 'bitcoin-explained'],
    icon: '🎓',
    color: '#10b981',
    difficulty: 'beginner'
  },
  {
    id: 'portfolio-master',
    title: '💼 Portfolio Master',
    description: 'Master portfolio management and risk',
    courses: ['portfolio-basics', 'risk-management', 'diversification'],
    icon: '💼',
    color: '#6366f1',
    difficulty: 'intermediate'
  },
  {
    id: 'technical-trader',
    title: '📈 Technical Trader',
    description: 'Learn chart analysis and trading strategies',
    courses: ['candlesticks', 'indicators', 'trading-strategies'],
    icon: '📈',
    color: '#f59e0b',
    difficulty: 'intermediate'
  },
  {
    id: 'defi-expert',
    title: '🚀 DeFi Expert',
    description: 'Master decentralized finance',
    courses: ['defi-intro', 'yield-farming', 'smart-contracts'],
    icon: '🚀',
    color: '#8b5cf6',
    difficulty: 'advanced'
  }
];

const DEFAULT_ACHIEVEMENTS: Achievement[] = [
  { id: 'first-course', title: 'First Steps', description: 'Complete your first course', icon: '🎉', unlocked: false },
  { id: 'marathon', title: 'Marathon Runner', description: 'Watch 10 courses', icon: '🏃', unlocked: false },
  { id: 'early-bird', title: 'Early Bird', description: 'Study for 3 consecutive days', icon: '🌅', unlocked: false },
  { id: 'master', title: 'Master Trader', description: 'Complete all advanced courses', icon: '🏆', unlocked: false },
  { id: 'explorer', title: 'Explorer', description: 'Try all course categories', icon: '🗺️', unlocked: false }
];

export function AcademyV2() {
  const toast = useToast();
  // State
  const [currentVideo, setCurrentVideo] = useState<Course | null>(null);
  const [userProgress, setUserProgress] = useState<Record<string, UserProgress>>({});
  const [myList, setMyList] = useState<string[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>(DEFAULT_ACHIEVEMENTS);
  const [showStats, setShowStats] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');

  // Load user data from localStorage
  useEffect(() => {
    try {
      const savedProgress = localStorage.getItem(STORAGE_KEYS.PROGRESS);
      if (savedProgress) {
        setUserProgress(JSON.parse(savedProgress));
      }

      const savedMyList = localStorage.getItem(STORAGE_KEYS.MY_LIST);
      if (savedMyList) {
        setMyList(JSON.parse(savedMyList));
      }

      const savedAchievements = localStorage.getItem(STORAGE_KEYS.ACHIEVEMENTS);
      if (savedAchievements) {
        setAchievements(JSON.parse(savedAchievements));
      }
    } catch (error) {
      console.error('Error loading Academy data:', error);
    }
  }, []);

  // Calculate user stats
  const userStats = useMemo(() => {
    const completed = Object.values(userProgress).filter(p => p.isCompleted).length;
    const inProgress = Object.values(userProgress).filter(p => !p.isCompleted && p.completionPercentage > 0).length;
    const totalWatchTime = Object.values(userProgress).reduce((sum, p) => sum + (p.currentTime || 0), 0);
    const unlockedAchievements = achievements.filter(a => a.unlocked).length;

    return {
      completed,
      inProgress,
      totalWatchTime: Math.floor(totalWatchTime / 60), // minutes
      totalCourses: MOCK_COURSES.length,
      completionRate: (completed / MOCK_COURSES.length) * 100,
      achievements: unlockedAchievements
    };
  }, [userProgress, achievements]);

  // Save progress
  const saveProgress = (courseId: string, progress: Partial<UserProgress>) => {
    setUserProgress(prev => {
      const updated = {
        ...prev,
        [courseId]: {
          userId: 'default',
          courseId,
          currentTime: 0,
          totalDuration: 0,
          completionPercentage: 0,
          lastWatchedAt: new Date(),
          startedAt: prev[courseId]?.startedAt || new Date(),
          isCompleted: false,
          ...prev[courseId],
          ...progress
        }
      };
      localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(updated));

      // Check achievements
      checkAchievements(updated);

      return updated;
    });
  };

  const toggleMyList = (courseId: string) => {
    setMyList(prev => {
      const updated = prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId];
      localStorage.setItem(STORAGE_KEYS.MY_LIST, JSON.stringify(updated));
      return updated;
    });
  };

  const playCourse = (course: Course) => {
    setCurrentVideo(course);

    if (!userProgress[course.id]) {
      saveProgress(course.id, {
        completionPercentage: 0,
        startedAt: new Date(),
        lastWatchedAt: new Date()
      });
    }
  };

  // Check and unlock achievements
  const checkAchievements = (progress: Record<string, UserProgress>) => {
    const newAchievements = [...achievements];
    let changed = false;

    // First course
    if (!newAchievements[0].unlocked && Object.values(progress).some(p => p.isCompleted)) {
      newAchievements[0].unlocked = true;
      newAchievements[0].unlockedAt = new Date();
      changed = true;
    }

    // Marathon (10 courses)
    if (!newAchievements[1].unlocked && Object.values(progress).filter(p => p.isCompleted).length >= 10) {
      newAchievements[1].unlocked = true;
      newAchievements[1].unlockedAt = new Date();
      changed = true;
    }

    if (changed) {
      setAchievements(newAchievements);
      localStorage.setItem(STORAGE_KEYS.ACHIEVEMENTS, JSON.stringify(newAchievements));
    }
  };

  // Filtered courses
  const filteredCourses = useMemo(() => {
    return MOCK_COURSES.filter(course => {
      const matchesSearch = searchQuery === '' ||
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesLevel = filterLevel === 'all' || course.level === filterLevel;

      return matchesSearch && matchesLevel;
    });
  }, [searchQuery, filterLevel]);

  // Course rows data
  const featuredCourse = useMemo(() => getFeaturedCourses()[0], []);
  const continueWatchingCourses = useMemo(() => {
    return MOCK_COURSES
      .filter(course => userProgress[course.id] && !userProgress[course.id].isCompleted)
      .sort((a, b) => {
        const aTime = userProgress[a.id]?.lastWatchedAt?.getTime() || 0;
        const bTime = userProgress[b.id]?.lastWatchedAt?.getTime() || 0;
        return bTime - aTime;
      });
  }, [userProgress]);

  const myListCourses = useMemo(() => {
    return MOCK_COURSES.filter(course => myList.includes(course.id));
  }, [myList]);

  const progressMap = useMemo(() => {
    const map: Record<string, number> = {};
    Object.entries(userProgress).forEach(([courseId, progress]) => {
      map[courseId] = progress.completionPercentage;
    });
    return map;
  }, [userProgress]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f0f0f',
      paddingBottom: '60px'
    }}>
      {/* Header with Stats Toggle */}
      <div style={{
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'white', margin: 0 }}>
          🎓 Edouard.ai Academy
        </h1>
        <button
          onClick={() => setShowStats(!showStats)}
          style={{
            padding: '10px 20px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icons.BarChart size={16} />
          {showStats ? 'Hide Stats' : 'Show Stats'}
        </button>
      </div>

      {/* Stats Dashboard */}
      {showStats && (
        <div style={{
          padding: '20px',
          background: 'rgba(26, 26, 26, 0.8)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h2 style={{ fontSize: '20px', color: 'white', marginBottom: '20px', fontWeight: 600 }}>
            📊 Your Learning Journey
          </h2>

          {/* Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                {userStats.completed}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                Courses Completed
              </div>
            </div>

            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                {userStats.inProgress}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                In Progress
              </div>
            </div>

            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                {userStats.totalWatchTime}m
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                Watch Time
              </div>
            </div>

            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                {userStats.achievements}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                Achievements
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '13px'
            }}>
              <span>Overall Progress</span>
              <span>{userStats.completionRate.toFixed(1)}%</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${userStats.completionRate}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {/* Achievements */}
          <div>
            <h3 style={{ fontSize: '16px', color: 'white', marginBottom: '12px', fontWeight: 600 }}>
              🏆 Achievements
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '12px'
            }}>
              {achievements.map(achievement => (
                <div
                  key={achievement.id}
                  style={{
                    padding: '12px',
                    background: achievement.unlocked
                      ? 'rgba(16, 185, 129, 0.2)'
                      : 'rgba(255,255,255,0.05)',
                    border: achievement.unlocked
                      ? '1px solid rgba(16, 185, 129, 0.4)'
                      : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    opacity: achievement.unlocked ? 1 : 0.5
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                    {achievement.icon}
                  </div>
                  <div style={{ fontSize: '13px', color: 'white', fontWeight: 600, marginBottom: '4px' }}>
                    {achievement.title}
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                    {achievement.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Learning Paths */}
      <div style={{ padding: '20px' }}>
        <h2 style={{ fontSize: '22px', color: 'white', marginBottom: '20px', fontWeight: 600 }}>
          🗺️ Learning Paths
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
          marginBottom: '40px'
        }}>
          {LEARNING_PATHS.map(path => {
            const completedCourses = path.courses.filter(
              courseId => userProgress[courseId]?.isCompleted
            ).length;
            const pathProgress = (completedCourses / path.courses.length) * 100;

            return (
              <div
                key={path.id}
                style={{
                  padding: '20px',
                  background: 'rgba(26, 26, 26, 0.8)',
                  border: `2px solid ${selectedPath === path.id ? path.color : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSelectedPath(selectedPath === path.id ? null : path.id)}
              >
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>
                  {path.icon}
                </div>
                <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '8px', fontWeight: 600 }}>
                  {path.title}
                </h3>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>
                  {path.description}
                </p>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.6)'
                  }}>
                    <span>{completedCourses}/{path.courses.length} courses</span>
                    <span>{pathProgress.toFixed(0)}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${pathProgress}%`,
                      height: '100%',
                      background: path.color,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
                <div style={{
                  padding: '6px 12px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  fontSize: '11px',
                  color: 'white',
                  display: 'inline-block',
                  textTransform: 'uppercase',
                  fontWeight: 600
                }}>
                  {path.difficulty}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search & Filters */}
      <div style={{ padding: '0 20px 20px', marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="🔍 Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: '1 1 300px',
              padding: '12px 16px',
              background: 'rgba(26, 26, 26, 0.8)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px'
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            {['all', 'beginner', 'intermediate', 'advanced'].map(level => (
              <button
                key={level}
                onClick={() => setFilterLevel(level as any)}
                style={{
                  padding: '10px 16px',
                  background: filterLevel === level ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Banner */}
      {featuredCourse && searchQuery === '' && (
        <div style={{ padding: '0 20px', marginBottom: '40px' }}>
          <HeroBanner
            course={featuredCourse}
            onPlay={() => playCourse(featuredCourse)}
            onMoreInfo={() => {
              toast.info(`More info about: ${featuredCourse.title}`);
            }}
            onAddToList={() => toggleMyList(featuredCourse.id)}
            isInList={myList.includes(featuredCourse.id)}
          />
        </div>
      )}

      {/* Course Rows */}
      {searchQuery === '' ? (
        <>
          {/* Continue Watching */}
          {continueWatchingCourses.length > 0 && (
            <CourseRow
              title="▶️ Continue Watching"
              courses={continueWatchingCourses}
              userProgress={progressMap}
              myList={myList}
              onPlayCourse={playCourse}
              onAddToList={toggleMyList}
            />
          )}

          {/* My List */}
          {myListCourses.length > 0 && (
            <CourseRow
              title="📚 My List"
              courses={myListCourses}
              userProgress={progressMap}
              myList={myList}
              onPlayCourse={playCourse}
              onAddToList={toggleMyList}
            />
          )}

          {/* Trending */}
          <CourseRow
            title="🔥 Trending Now"
            courses={getTrendingCourses()}
            userProgress={progressMap}
            myList={myList}
            onPlayCourse={playCourse}
            onAddToList={toggleMyList}
          />

          {/* Beginner */}
          <CourseRow
            title="🎓 Beginner Friendly"
            courses={getCoursesByLevel('beginner')}
            userProgress={progressMap}
            myList={myList}
            onPlayCourse={playCourse}
            onAddToList={toggleMyList}
          />

          {/* Portfolio */}
          <CourseRow
            title="💼 Portfolio Management"
            courses={getCoursesByCategory('portfolio-management')}
            userProgress={progressMap}
            myList={myList}
            onPlayCourse={playCourse}
            onAddToList={toggleMyList}
          />

          {/* Technical Analysis */}
          <CourseRow
            title="📈 Technical Analysis"
            courses={getCoursesByCategory('technical-analysis')}
            userProgress={progressMap}
            myList={myList}
            onPlayCourse={playCourse}
            onAddToList={toggleMyList}
          />

          {/* Advanced */}
          <CourseRow
            title="🚀 Advanced Trading"
            courses={getCoursesByCategory('advanced-trading')}
            userProgress={progressMap}
            myList={myList}
            onPlayCourse={playCourse}
            onAddToList={toggleMyList}
          />
        </>
      ) : (
        // Search Results
        <div style={{ padding: '0 20px' }}>
          <h3 style={{ fontSize: '20px', color: 'white', marginBottom: '20px' }}>
            {filteredCourses.length} results for "{searchQuery}"
          </h3>
          <CourseRow
            title=""
            courses={filteredCourses}
            userProgress={progressMap}
            myList={myList}
            onPlayCourse={playCourse}
            onAddToList={toggleMyList}
          />
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '80px',
        padding: '40px 60px',
        background: 'rgba(26, 26, 26, 0.5)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        textAlign: 'center'
      }}>
        <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', color: 'var(--accent)' }}>
          🎓 Edouard.ai Academy
        </h3>
        <p style={{ fontSize: '16px', color: 'var(--muted)', marginBottom: '8px' }}>
          Master crypto trading and portfolio management with expert-curated content
        </p>
        <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
          {MOCK_COURSES.length} courses • {achievements.filter(a => a.unlocked).length}/{achievements.length} achievements unlocked
        </p>
      </div>

      {/* Video Player */}
      {currentVideo && (
        <VideoPlayerModal
          videoUrl={currentVideo.videoUrl || ''}
          title={currentVideo.title}
          onClose={() => {
            setCurrentVideo(null);
          }}
          startTime={userProgress[currentVideo.id]?.currentTime || 0}
        />
      )}
    </div>
  );
}
